import { memo, useCallback } from 'react'

import { Box, IconButton, SvgIcon, Tooltip, Typography } from '@mui/material'
import { ImagesGetRequest } from '@nativewaves/nw-cloud-sdk/content'
import { useNavigate } from '@tanstack/react-router'
import { ArrowDownCircleFill } from 'emotion-icons/bootstrap'
import { omit, omitBy, pickBy } from 'lodash-es'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { imagesOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images'

type SortableHeaderProps = {
  title: string
  sortProp: keyof PickStartsWith<ImagesGetRequest, 'orderBy'>
}

const SortableHeaderRaw = ({ title, sortProp }: SortableHeaderProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])

  const imageRoute = imagesOverviewRoute

  const navigate = useNavigate({ from: imageRoute.fullPath })
  const search = imageRoute.useSearch({
    select: (items) =>
      pickBy(items, (_, key) => key.startsWith('orderBy')) as PickStartsWith<ImagesGetRequest, 'orderBy'>,
    structuralSharing: true,
  })

  const handleSorting = useCallback(
    (sortProperty: keyof PickStartsWith<ImagesGetRequest, 'orderBy'>) => {
      if (sortProperty in search) {
        if (search[sortProperty] === 'Desc') {
          navigate({ to: imageRoute.to, search: (current) => omit(current, sortProperty) })
        } else {
          navigate({ to: imageRoute.to, search: (current) => ({ ...current, [sortProperty]: 'Desc' }) })
        }
      } else {
        navigate({
          to: imageRoute.to,
          search: (current) => ({ ...omitBy(current, (_, key) => key.startsWith('orderBy')), [sortProperty]: 'Asc' }),
        })
      }
    },
    [navigate, search, imageRoute],
  )

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Typography variant="inherit">{title}</Typography>
      <Tooltip
        title={
          !(sortProp in search)
            ? t('sortAscending')
            : search[sortProp] === 'Asc'
              ? t('sortDescending')
              : t('sortDisable')
        }
      >
        <IconButton size="small" onClick={() => handleSorting(sortProp)}>
          <SvgIcon
            fontSize="inherit"
            color={search[sortProp] ? 'primary' : 'inherit'}
            component={motion.svg}
            animate={search[sortProp] === 'Desc' ? { rotateZ: '180deg' } : { rotateZ: '0deg' }}
          >
            <ArrowDownCircleFill />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SortableHeader = memo(SortableHeaderRaw)
